import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { setSubmission, startStatus, WorkflowError } from '../engine/workflowEngine'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { logWarehouseId } from './c07.shared'
import { notify, wmsRoute } from './notification.shared'
import { toErrorResponse } from './workflow.handler'

/**
 * Endpoint custom C05 Return Order RM (WF-RTN-01, entity `return_order`):
 * - RTN-01 `create` (satu RO per supplier dari tiket Open, lahir Draft), RTN-02 `attach-tickets` / `detach-ticket`,
 *   `approve-purchasing` (ACT_RTN_APP_PURCH + tanggal rencana), `reschedule`, `assign-pickup` (ACT_RTN_ASSIGN +
 *   tugas RNP), `record-shipment` (ACT_RTN_RECORD_SHIP + SJR), `cancel` (Draft / Siap Kirim → tugas Penarikan).
 * - RTN-04 `pickup-move`: satu unit NG berpindah QN-NG → ZS-RM (Pengambilan) atau sebaliknya (Penarikan);
 *   saat unit terakhir pindah tugas selesai dan RO → Siap Kirim (`ACT_RTN_TO_STAGING`).
 * Aksi persetujuan Supervisor/Kadiv/Reject/Ajukan memakai `POST /api/workflow/submission/v2` standar.
 */

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const ok = (data: unknown) => [200, { statusCode: 200, success: true, data }]

function fail(message: string, code = 'ERR_VALIDATION_FAILED', status = 400): never {
  throw new WorkflowError(message, code, status)
}

const pad = (value: number, width: number) => String(value).padStart(width, '0')

export const RTN_WORKFLOW_CODE = 'WF-RTN-01'
const NG_ZONE = 'QN-NG'
const STAGING_ZONE = 'ZS-RM'
export const EDITABLE_STATUSES = ['RTN_DRAFT', 'RTN_REJECTED']

/* ------------------------------------------------------------ referensi */

function roleCodesOf(userId: string): string[] {
  return readTable('mt_privileges')
    .filter(row => idOf(row.user) === userId)
    .map(row => String(findRow('mt_roles', idOf(row.role))?.code ?? ''))
}

const RO_CREATOR_ROLES = ['admin_wh_rm', 'wh_rm_supervisor', 'kadiv_wh_rm']

/** Actor workflow untuk pemilik draft sesuai peran yang sedang login. */
function ownerActor() {
  const codes = roleCodesOf(currentUserId())
  if (codes.includes('admin_wh_rm')) return 'ACT_RTN_ADMIN_RM'
  return codes.includes('wh_rm_supervisor') ? 'ACT_RTN_SPV_OWNER' : 'ACT_RTN_KADIV_OWNER'
}

function requireRole(message: string, ...roles: string[]) {
  if (!roleCodesOf(currentUserId()).some(code => roles.includes(code))) fail(message, 'ERR_UNAUTHORIZED_ROLE', 403)
}

const workflow = () => {
  const row = readTable('wml_workflow').find(item => item.code === RTN_WORKFLOW_CODE)
  if (!row) fail('Workflow WF-RTN-01 tidak ditemukan.', 'WORKFLOW_NOT_FOUND', 404)
  return row
}

const statusCodeOf = (ro: LocalRow) => String(findRow('wml_status', idOf(ro.status_id))?.code ?? '')

function requireOrder(roId: unknown) {
  const ro = findRow('return_order', String(roId ?? ''))
  if (!ro) fail('Return Order tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return ro
}

const actionOf = (name: string) => {
  const row = readTable('wml_action').find(item => item.name === name && idOf(item.workflow_id) === workflow().id)
  if (!row) fail(`Action ${name} tidak ditemukan.`, 'ACTION_NOT_FOUND', 404)
  return row
}

/** Actor WF-RTN-01 milik persona aktif (role cocok). */
function actorFor(code: string) {
  const roles = readTable('mt_privileges')
    .filter(row => idOf(row.user) === currentUserId())
    .map(row => idOf(row.role))
  const actor = readTable('wml_actor').find(
    row => row.code === code && idOf(row.workflow_id) === workflow().id && roles.includes(idOf(row.role_id))
  )
  if (!actor) fail('Actor tidak berhak menjalankan aksi ini.', 'ERR_UNAUTHORIZED_ROLE', 403)
  return actor
}

function runAction(ro: LocalRow, name: string, actorCode: string, patch: Record<string, unknown> = {}, reason = '') {
  const action = actionOf(name)
  return setSubmission({
    workflow_id: workflow().id,
    action_id: action.id,
    submission_id: idOf(ro.submission_id) || null,
    entity_id: ro.id,
    actor_id: actorFor(actorCode).id,
    reason_text: reason,
    native_document_payload: { path: 'return_order', payload: { id: ro.id, ...patch } }
  })
}

function nextDocNo(prefix: string, collection: string, field: string, date = new Date()) {
  const day = `${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`
  const pattern = new RegExp(`^${prefix}-${day}-(\\d{3})`)
  const highest = readTable(collection)
    .map(row => pattern.exec(String(row[field] ?? '')))
    .reduce((max, match) => (match ? Math.max(max, Number(match[1])) : max), 0)
  return `${prefix}-${day}-${pad(highest + 1, 3)}`
}

/** `nnn/SJR/MM/YYYY` — urut per bulan (BA RTN-02 modal "Catat Pengiriman"). */
function nextSjrNo(date = new Date()) {
  const suffix = `/SJR/${pad(date.getMonth() + 1, 2)}/${date.getFullYear()}`
  const highest = readTable('return_order')
    .map(row => String(row.sjr_no ?? ''))
    .filter(value => value.endsWith(suffix))
    .reduce((max, value) => Math.max(max, Number(value.slice(0, 3))), 0)
  return `${pad(highest + 1, 3)}${suffix}`
}

function txLog(action: string, docRef: string, detail: string) {
  insertRow('tx_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    log_type: 'Return Order',
    page_code: 'RTN-02',
    action,
    doc_ref: docRef,
    detail,
    warehouse_id: logWarehouseId('RTN-02'),
    user_id: currentUserId()
  })
}

const zoneByCode = (code: string) => readTable('warehouse_zone').find(zone => String(zone.code).toUpperCase() === code)

const linesOf = (ro: LocalRow, includeReleased = false) =>
  readTable('return_order_line').filter(
    line => idOf(line.ro_id) === ro.id && (includeReleased || line.status !== 'RELEASED')
  )

const ticketOf = (line: LocalRow) => findRow('ng_ticket', idOf(line.ng_ticket_id))

/** Unit NG sebuah RO (urut baris). */
export const unitsOf = (ro: LocalRow) =>
  linesOf(ro)
    .map(line => findRow('rm_unit', idOf(ticketOf(line)?.unit_id)))
    .filter((unit): unit is LocalRow => Boolean(unit))

/* ------------------------------------------------------------ RTN-01 / RTN-02 draft */

type CreateBody = { ticket_ids?: string[] }

/** Gabungkan tiket Open terpilih → satu RO Draft per supplier (supplier dari batch kedatangan, bukan master). */
export function createReturnOrders(body: CreateBody) {
  requireRole('Return Order dibuat oleh Admin, Supervisor, atau Kadiv WH RM.', ...RO_CREATOR_ROLES)
  const ids = [...new Set(body.ticket_ids ?? [])]
  if (!ids.length) fail('Pilih minimal satu tiket.')
  const tickets = ids.map(id => {
    const ticket = findRow('ng_ticket', id)
    if (!ticket) fail('Tiket NG tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    if (ticket.ticket_status !== 'OPEN') fail(`Tiket ${ticket.ticket_no} sudah tidak Open.`)
    return ticket
  })
  const bySupplier = new Map<string, LocalRow[]>()
  tickets.forEach(ticket => {
    const supplier = idOf(ticket.supplier_id)
    bySupplier.set(supplier, [...(bySupplier.get(supplier) ?? []), ticket])
  })
  const start = startStatus(workflow().id)
  const orders = [...bySupplier.entries()].map(([supplier, group]) => {
    const roId = newId()
    const submission = insertRow('wxl_submission', {
      ...auditFieldsForCreate(),
      id: newId(),
      workflow_id: workflow().id,
      entity: 'return_order',
      entity_id: roId,
      source: 'ACTION',
      current_status_id: start?.id ?? null,
      last_action_id: null,
      workflow_snapshot: null
    })
    const ro = insertRow('return_order', {
      ...auditFieldsForCreate(),
      id: roId,
      ro_no: nextDocNo('RO', 'return_order', 'ro_no'),
      supplier_id: supplier,
      status_id: start?.id ?? null,
      submission_id: submission.id,
      plan_date: null,
      plan_note: null,
      preparer_id: null,
      shipped_at: null,
      carrier: null,
      sjr_no: null,
      is_shipped: false
    })
    group.forEach(ticket => {
      insertRow('return_order_line', {
        ...auditFieldsForCreate(),
        id: newId(),
        ro_id: roId,
        ng_ticket_id: ticket.id,
        status: 'ATTACHED'
      })
      updateRow('ng_ticket', ticket.id, { ticket_status: 'ATTACHED', ...auditFieldsForUpdate() })
    })
    const supplierName = findRow('mt_companies', supplier)?.name ?? supplier
    txLog(
      'ro-create',
      String(ro.ro_no),
      `RO ${ro.ro_no} (${supplierName}) dibuat dari ${group.length} tiket NG — supplier ditelusuri dari batch kedatangan`
    )
    return { id: roId, ro_no: ro.ro_no, supplier_name: supplierName, ticket_count: group.length }
  })
  return { orders }
}

type TicketsBody = { ro_id?: string; ticket_ids?: string[] }

function requireEditable(ro: LocalRow) {
  requireRole('Hanya Admin, Supervisor, atau Kadiv WH RM yang bisa mengubah isi RO.', ...RO_CREATOR_ROLES)
  if (!EDITABLE_STATUSES.includes(statusCodeOf(ro)))
    fail('RO yang sedang berjalan tidak bisa diubah isinya.', 'INVALID_FROM_STATUS', 409)
}

/** Tambah tiket Open satu supplier ke RO Draft/Rejected. */
export function attachTickets(body: TicketsBody) {
  const ro = requireOrder(body.ro_id)
  requireEditable(ro)
  const ids = body.ticket_ids ?? []
  if (!ids.length) fail('Pilih minimal satu tiket.')
  ids.forEach(id => {
    const ticket = findRow('ng_ticket', id)
    if (!ticket || ticket.ticket_status !== 'OPEN') fail('Tiket tidak tersedia (bukan Open).')
    if (idOf(ticket.supplier_id) !== idOf(ro.supplier_id)) fail('Tiket harus dari supplier yang sama dengan RO.')
    insertRow('return_order_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      ro_id: ro.id,
      ng_ticket_id: ticket.id,
      status: 'ATTACHED'
    })
    updateRow('ng_ticket', ticket.id, { ticket_status: 'ATTACHED', ...auditFieldsForUpdate() })
  })
  txLog('ro-attach', String(ro.ro_no), `${ids.length} tiket ditambahkan ke ${ro.ro_no}`)
  return { ro_id: ro.id }
}

/** Lepas satu tiket dari RO Draft/Rejected → tiket kembali Open. */
export function detachTicket(body: { ro_id?: string; ticket_id?: string }) {
  const ro = requireOrder(body.ro_id)
  requireEditable(ro)
  const line = linesOf(ro).find(item => idOf(item.ng_ticket_id) === body.ticket_id)
  if (!line) fail('Tiket tidak ada di RO ini.', 'ERR_NOT_FOUND', 404)
  updateRow('return_order_line', line.id, { status: 'RELEASED', ...auditFieldsForUpdate() })
  const ticket = ticketOf(line)
  if (ticket) updateRow('ng_ticket', ticket.id, { ticket_status: 'OPEN', ...auditFieldsForUpdate() })
  txLog('ro-detach', String(ro.ro_no), `Tiket ${ticket?.ticket_no} dilepas dari ${ro.ro_no}`)
  return { ro_id: ro.id }
}

/* ------------------------------------------------------------ approval Purchasing & jadwal */

const todayIso = () => new Date().toISOString().slice(0, 10)

export function approvePurchasing(body: { ro_id?: string; plan_date?: string; plan_note?: string }) {
  const ro = requireOrder(body.ro_id)
  const planDate = String(body.plan_date ?? '').trim()
  if (!planDate) fail('Tanggal rencana retur wajib diisi.')
  const result = runAction(ro, 'ACT_RTN_APP_PURCH', 'ACT_RTN_PURCHASING', {
    plan_date: planDate,
    plan_note: String(body.plan_note ?? '').trim() || null
  })
  txLog('ro-approve-purch', String(ro.ro_no), `Approved Purchasing — rencana retur ${planDate}`)
  return result
}

export function rescheduleReturn(body: { ro_id?: string; plan_date?: string; reason?: string }) {
  requireRole('Jadwal retur digeser oleh Tim Purchasing.', 'purchasing')
  const ro = requireOrder(body.ro_id)
  if (!['RTN_APPROVED', 'RTN_IN_PROGRESS'].includes(statusCodeOf(ro)))
    fail('Jadwal hanya bisa digeser sebelum unit tiba di Staging.', 'INVALID_FROM_STATUS', 409)
  const planDate = String(body.plan_date ?? '').trim()
  const reason = String(body.reason ?? '').trim()
  if (!planDate || !reason) fail('Tanggal baru & alasan wajib diisi.')
  updateRow('return_order', ro.id, {
    plan_date: planDate,
    plan_note: `Ditunda: ${reason}`,
    ...auditFieldsForUpdate()
  })
  txLog('ro-reschedule', String(ro.ro_no), `Jadwal retur digeser ke ${planDate} — ${reason}`)
  return { ro_id: ro.id, plan_date: planDate }
}

/* ------------------------------------------------------------ penugasan pengambilan */

function createPickupTask(ro: LocalRow, direction: 'PICKUP' | 'PULL_BACK', operatorId: string, note: string | null) {
  return insertRow('rm_ng_pickup_task', {
    ...auditFieldsForCreate(),
    id: newId(),
    task_no: nextDocNo('RNP', 'rm_ng_pickup_task', 'task_no'),
    ro_id: ro.id,
    direction,
    operator_id: operatorId,
    task_status: 'ASSIGNED',
    note
  })
}

export function assignPickup(body: { ro_id?: string; operator_id?: string; note?: string }) {
  requireRole('Penugasan pengambilan NG oleh Admin atau Supervisor WH RM.', 'admin_wh_rm', 'wh_rm_supervisor')
  const ro = requireOrder(body.ro_id)
  const operator = findRow('directus_users', String(body.operator_id ?? ''))
  if (!operator || !roleCodesOf(operator.id).includes('operator_wh_rm'))
    fail('Tidak ada operator WH RM di master user.')
  const result = runAction(
    ro,
    'ACT_RTN_ASSIGN',
    roleCodesOf(currentUserId()).includes('admin_wh_rm') ? 'ACT_RTN_ADMIN_RM' : 'ACT_RTN_SPV_RM',
    { preparer_id: operator.id }
  )
  const task = createPickupTask(ro, 'PICKUP', operator.id, String(body.note ?? '').trim() || null)
  txLog('ro-assign', String(ro.ro_no), `Tugas ${task.task_no} → ${operator.first_name} (${unitsOf(ro).length} unit NG)`)
  notify({
    to: { userIds: [String(operator.id)] },
    type: 'RO_PICKUP_ASSIGNED',
    title: `Tugas pengambilan NG ${task.task_no} untuk Anda`,
    body: `Return Order ${ro.ro_no}: ${unitsOf(ro).length} unit NG perlu diambil — buka Pengambilan NG.`,
    link: wmsRoute('return-order-rm/pengambilan-ng'),
    ref: { type: 'return_order', id: String(ro.id) }
  })
  return { ...result, task_id: task.id, task_no: task.task_no }
}

/* ------------------------------------------------------------ pengiriman & pembatalan */

export function recordShipment(body: { ro_id?: string; shipped_at?: string; carrier?: string }) {
  const ro = requireOrder(body.ro_id)
  const shippedAt = String(body.shipped_at ?? '').trim() || todayIso()
  const carrier = String(body.carrier ?? '').trim()
  if (!carrier) fail('Pengangkut / ekspedisi wajib diisi.')
  const sjr = nextSjrNo(new Date(shippedAt))
  const result = runAction(ro, 'ACT_RTN_RECORD_SHIP', 'ACT_RTN_ADMIN_RM', {
    shipped_at: new Date(shippedAt).toISOString(),
    carrier,
    sjr_no: sjr
  })
  unitsOf(ro).forEach(unit => updateRow('rm_unit', unit.id, { unit_status: 'RETURNED', ...auditFieldsForUpdate() }))
  linesOf(ro).forEach(line => {
    const ticket = ticketOf(line)
    if (ticket) updateRow('ng_ticket', ticket.id, { ticket_status: 'RETURNED', ...auditFieldsForUpdate() })
  })
  txLog('ro-ship', String(ro.ro_no), `Pengiriman tercatat — Surat Jalan ${sjr}, ${carrier}`)
  return { ...result, sjr_no: sjr }
}

/** Batalkan RO: Draft → Cancelled; Siap Kirim → Cancelled + tugas Penarikan ke operator pengambilan. */
export function cancelReturn(body: { ro_id?: string; reason?: string }) {
  const ro = requireOrder(body.ro_id)
  const reason = String(body.reason ?? '').trim()
  if (!reason) fail('Alasan pembatalan wajib diisi.')
  const status = statusCodeOf(ro)
  if (status === 'RTN_DRAFT') {
    const result = runAction(ro, 'ACT_RTN_CANCEL_DRAFT', ownerActor(), {}, reason)
    linesOf(ro).forEach(line => {
      updateRow('return_order_line', line.id, { status: 'RELEASED', ...auditFieldsForUpdate() })
      const ticket = ticketOf(line)
      if (ticket) updateRow('ng_ticket', ticket.id, { ticket_status: 'OPEN', ...auditFieldsForUpdate() })
    })
    txLog('ro-cancel', String(ro.ro_no), `RO dibatalkan — ${reason}`)
    return { ...result, pull_back_task_no: null }
  }
  if (status !== 'RTN_READY_STAGING')
    fail('RO ini tidak bisa dibatalkan pada status sekarang.', 'INVALID_FROM_STATUS', 409)
  const result = runAction(ro, 'ACT_RTN_CANCEL_STAGING', 'ACT_RTN_ADMIN_RM', {}, reason)
  const previous = readTable('rm_ng_pickup_task').find(
    task => idOf(task.ro_id) === ro.id && task.direction === 'PICKUP'
  )
  const operatorId = idOf(previous?.operator_id ?? ro.preparer_id)
  const task = createPickupTask(ro, 'PULL_BACK', operatorId, `Penarikan — RO dibatalkan: ${reason}`)
  txLog('ro-cancel', String(ro.ro_no), `RO dibatalkan — ${reason}; tugas penarikan ${task.task_no}`)
  return { ...result, pull_back_task_no: task.task_no }
}

/* ------------------------------------------------------------ RTN-04 pindah unit */

type MoveBody = { task_id?: string; unit_id?: string }

/** Satu unit NG selesai tiga pindaian → zona unit berpindah; tugas selesai saat unit terakhir. */
export function moveNgUnit(body: MoveBody) {
  const me = currentUserId()
  const task = findRow('rm_ng_pickup_task', String(body.task_id ?? ''))
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (!roleCodesOf(me).includes('operator_wh_rm') || idOf(task.operator_id) !== me)
    fail('Hanya operator pemilik tugas yang bisa scan.', 'ERR_UNAUTHORIZED_ROLE', 403)
  if (task.task_status === 'COMPLETED') fail('Tugas ini sudah selesai.')
  const ro = requireOrder(idOf(task.ro_id))
  const pullBack = task.direction === 'PULL_BACK'
  const unit = findRow('rm_unit', String(body.unit_id ?? ''))
  const lineUnits = linesOf(ro, pullBack).map(line => findRow('rm_unit', idOf(ticketOf(line)?.unit_id)))
  if (!unit || !lineUnits.some(item => item?.id === unit.id)) fail('Unit bukan bagian dari Return Order ini.')
  const from = zoneByCode(pullBack ? STAGING_ZONE : NG_ZONE)
  const to = zoneByCode(pullBack ? NG_ZONE : STAGING_ZONE)
  if (idOf(unit.warehouse_zone_id) !== from?.id) fail(`Unit ${unit.qr_code} tidak berada di ${from?.name}.`)
  updateRow('rm_unit', unit.id, {
    warehouse_zone_id: to?.id ?? null,
    unit_status: pullBack ? 'NG_IN_QUARANTINE' : 'NG_IN_RETURN_STAGING',
    ...auditFieldsForUpdate()
  })
  const ticket = readTable('ng_ticket').find(row => idOf(row.unit_id) === unit.id)
  if (ticket)
    updateRow('ng_ticket', ticket.id, { placement_warehouse_zone_id: to?.id ?? null, ...auditFieldsForUpdate() })
  insertRow('stock_movement', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    movement_type: pullBack ? 'NG_RETURN_PULLBACK' : 'NG_RETURN_PICKUP',
    unit_id: unit.id,
    qty_small: Number(unit.qty_small ?? 0),
    from_rack_bin_id: null,
    to_rack_bin_id: null,
    from_warehouse_zone_id: from?.id ?? null,
    to_warehouse_zone_id: to?.id ?? null,
    doc_ref: task.task_no
  })
  txLog(
    pullBack ? 'ng-pullback' : 'ng-pickup',
    String(unit.qr_code),
    `${unit.qr_code} ${from?.code} → ${to?.code} (tugas ${task.task_no}, ${ro.ro_no})`
  )

  const done = lineUnits.every(item => idOf(findRow('rm_unit', item?.id)?.warehouse_zone_id) === to?.id)
  updateRow('rm_ng_pickup_task', task.id, {
    task_status: done ? 'COMPLETED' : 'IN_PROGRESS',
    ...auditFieldsForUpdate()
  })
  let roStatus = statusCodeOf(requireOrder(ro.id))
  if (done && !pullBack) {
    runAction(ro, 'ACT_RTN_TO_STAGING', 'ACT_RTN_OP_RM')
    roStatus = 'RTN_READY_STAGING'
  }
  if (done && pullBack) {
    lineUnits.forEach(item => {
      const row = item ? readTable('ng_ticket').find(entry => idOf(entry.unit_id) === item.id) : undefined
      if (row) updateRow('ng_ticket', row.id, { ticket_status: 'OPEN', ...auditFieldsForUpdate() })
    })
    linesOf(ro, true).forEach(line => updateRow('return_order_line', line.id, { status: 'RELEASED' }))
  }
  return {
    task_id: task.id,
    task_done: done,
    ro_status: roStatus,
    moved: lineUnits.filter(item => idOf(findRow('rm_unit', item?.id)?.warehouse_zone_id) === to?.id).length,
    total: lineUnits.length
  }
}

/* ------------------------------------------------------------ registrasi */

const parse = <T>(data: unknown) => JSON.parse(String(data ?? '{}')) as T

const handle = (run: () => unknown) => {
  try {
    return ok(run())
  } catch (error) {
    return toErrorResponse(error)
  }
}

export function registerReturnOrderHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/return-order'
  mock.onPost(`${base}/create`).reply(config => handle(() => createReturnOrders(parse(config.data))))
  mock.onPost(`${base}/attach-tickets`).reply(config => handle(() => attachTickets(parse(config.data))))
  mock.onPost(`${base}/detach-ticket`).reply(config => handle(() => detachTicket(parse(config.data))))
  mock.onPost(`${base}/approve-purchasing`).reply(config => handle(() => approvePurchasing(parse(config.data))))
  mock.onPost(`${base}/reschedule`).reply(config => handle(() => rescheduleReturn(parse(config.data))))
  mock.onPost(`${base}/assign-pickup`).reply(config => handle(() => assignPickup(parse(config.data))))
  mock.onPost(`${base}/record-shipment`).reply(config => handle(() => recordShipment(parse(config.data))))
  mock.onPost(`${base}/cancel`).reply(config => handle(() => cancelReturn(parse(config.data))))
  mock.onPost(`${base}/pickup-move`).reply(config => handle(() => moveNgUnit(parse(config.data))))
}
